// Admin auth — bcrypt password gate.
//
// Wraps the kitchenos-api tRPC procedures used by admin:
//   - GET  /api/trpc/portal.auth.me           — session check
//   - POST /api/trpc/portal.auth.adminLogin   — bcrypt login
//   - POST /api/trpc/portal.auth.logout       — clear session cookie
//
// Cookie-based session via `kos_admin` (HttpOnly, managed server-side; see
// /var/www/kitchenos-api/server.js around line 1178). The admin token returns
// a synthetic user from `portal.auth.me`:
//   { id, email, name, role, isAdmin }
//
// The chrome calls `getCurrentUser()` once on boot to populate the in-memory
// cache, then uses `login()` / `logout()` to mutate it.

import { api, ApiError } from "@tkos/api-client";
import type { AdminUser } from "./types";

// ── Endpoints ────────────────────────────────────────────────────────────────
// kitchenos-api accepts the unbatched single-procedure shape `{ json: <input> }`.

const ADMIN_LOGIN_PATH = "/trpc/portal.auth.adminLogin";
const LOGOUT_PATH = "/trpc/portal.auth.logout";
const ME_PATH = "/trpc/portal.auth.me";

/** SuperJSON-wrapped tRPC success envelope returned by kitchenos-api. */
type TrpcEnvelope<T> =
  | Array<{ result: { data: { json: T } } }>
  | { result: { data: { json: T } } };

function unwrap<T>(envelope: TrpcEnvelope<T>): T {
  if (Array.isArray(envelope)) {
    return envelope[0]!.result.data.json;
  }
  return envelope.result.data.json;
}

/** Shape returned by portal.auth.me when authenticated as admin. */
interface RawMe {
  id: string;
  email: string;
  name?: string;
  role?: string;
  isAdmin?: boolean;
}

function toAdminUser(raw: RawMe | null): AdminUser | null {
  if (!raw) return null;
  return {
    id: raw.id,
    email: raw.email,
    name: raw.name ?? raw.email,
  };
}

// ── In-memory auth state ─────────────────────────────────────────────────────

let currentUser: AdminUser | null = null;
const listeners = new Set<(user: AdminUser | null) => void>();

/** Subscribe to auth-state changes. Returns an unsubscribe function. */
export function onAuthChange(fn: (user: AdminUser | null) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function setCurrentUser(user: AdminUser | null): void {
  currentUser = user;
  for (const fn of listeners) fn(user);
}

/** Synchronous getter for the cached user. Use after `getCurrentUser()`. */
export function getCachedUser(): AdminUser | null {
  return currentUser;
}

// ── Server calls ─────────────────────────────────────────────────────────────

/**
 * Validate the session by calling `portal.auth.me`. Updates the in-memory
 * user state and returns it (null if anonymous).
 *
 * The chrome calls this once on boot. On a subsequent 401 the chrome can call
 * this again to confirm we're logged out and re-render the login screen.
 */
export async function getCurrentUser(): Promise<AdminUser | null> {
  try {
    const env = await api<TrpcEnvelope<RawMe | null>>(ME_PATH, { method: "GET" });
    const me = unwrap(env);
    setCurrentUser(toAdminUser(me));
    return currentUser;
  } catch (err) {
    if (!(err instanceof ApiError) || err.status >= 500) {
      console.warn("[admin/auth] portal.auth.me failed:", err);
    }
    setCurrentUser(null);
    return null;
  }
}

/**
 * Submit the admin password. The `email` argument is accepted for forward
 * compatibility (the form collects it) but the server currently only checks
 * the password (bcrypt) — see `verifyAdminPassword` in modules/security.
 *
 * On success the server sets the `kos_admin` cookie. We then fetch
 * `portal.auth.me` to get the canonical user shape and cache it.
 */
export async function login(_email: string, password: string): Promise<AdminUser> {
  const body = JSON.stringify({ json: { password } });
  const env = await api<TrpcEnvelope<{ ok: boolean; isAdmin: boolean }>>(
    ADMIN_LOGIN_PATH,
    { method: "POST", body },
  ).catch((err: unknown) => {
    if (err instanceof ApiError && err.status === 401) {
      throw new Error("Incorrect password. Please try again.");
    }
    throw new Error("Connection error. Please try again.");
  });

  const result = unwrap(env);
  if (!result?.ok) {
    throw new Error("Login failed. Please try again.");
  }

  const me = await getCurrentUser();
  if (!me) {
    throw new Error("Logged in but could not load profile. Try refreshing.");
  }
  return me;
}

/** Sign out. Clears the cookie server-side and the cached user locally. */
export async function logout(): Promise<void> {
  try {
    await api<TrpcEnvelope<{ ok: boolean }>>(LOGOUT_PATH, { method: "POST" });
  } catch (err) {
    // Even if the network call fails, drop the local cache so the chrome
    // re-renders the login screen. The next /me call will reconcile.
    console.warn("[admin/auth] logout call failed:", err);
  }
  setCurrentUser(null);
}
